import { Components, Theme } from '@mui/material/styles'

import { FIELD_HEIGHT } from 'components/UI/fieldMetrics'

export const MuiInputBase: Components<Theme>['MuiInputBase'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      border: `1px solid ${theme.palette.grey[600]}`,
      padding: '0 14px',
      // 16, not 14. Below 16px iOS Safari ZOOMS THE PAGE when the field takes
      // focus, and the zoom persists after you navigate away — which is why a
      // certificate opened after using search appeared blown up. 16 is the
      // threshold, not a preference.
      fontSize: '16px',
      /*
       * `FIELD_HEIGHT`, not the 44 that was here.
       *
       * ⚠️ This was the reason the app had two field heights that no amount of
       * reading the components explained. `MuiInputBase.styleOverrides.root`
       * reaches EVERY input in the app — `components/UI/Input` (a FilledInput),
       * `@repo/ui`'s Input, every Select, MuiTelInput — and `min-height` is not
       * a default a component can override by declaring `height`: the used
       * height is `max(height, min-height)`, so a 44px floor SILENTLY BEAT every
       * `height: FIELD_HEIGHT` written downstream.
       *
       * So VERA-291's consolidation only ever landed on the controls that are
       * NOT MUI inputs. `FieldTrigger`, `PhoneField`'s outer box, `SelectDrawer`'s
       * trigger and the picker search boxes sat at 40; `Input`, `SortField`'s
       * Select and every form field rendered 44 while their source said 40. That
       * is the "field heights look inconsistent" report, and it could not be
       * fixed in any component — only here.
       *
       * The 48px band (`ToolbarSearchField`, `SearchFilterSortBar`,
       * `RequestAccessInfo`) is unaffected: those set an explicit 48, which is
       * above this floor either way. The OTP boxes ARE affected and are pinned
       * back to 44 in `MuiTextField.ts` — see the note there.
       */
      minHeight: `${FIELD_HEIGHT}px`,
      // Tracks the height: this is what vertically centres a single line of text
      // in the field, so leaving it at 44 in a 40px box offsets the text.
      lineHeight: `${FIELD_HEIGHT}px`,
      margin: 0,
      input: {
        height: `${FIELD_HEIGHT}px`,
        padding: '0',
        // Must match the root: iOS reads the INPUT's computed size.
        fontSize: '16px',
      },
      '&:before, &:after': {
        display: 'none',
      },
      'input:-webkit-autofill': {
        WebkitBackgroundClip: 'text',
      },
      /*
        INVALID. `!important` is what makes this render, and without it the rule was
        dead — which is how an errored field ended up pixel-identical to a valid one.

        Measured on the Profile "Edit name" drawer with `first_name` cleared and saved:
        the errored field's border computed to rgb(228,228,231) and the valid
        `last_name` beside it to rgb(228,228,231). The helper text underneath was the
        entire error signal. The declaration below existed the whole time and simply
        never won: `index.css`'s `#vera-shadow-root * { border-color: hsl(var(--border)) }`
        carries ID specificity, which beats any emotion class rule regardless of how the
        colour is written (see the long note in `index.css` and in `settings/tokens.ts`).
        `!important` is the documented escape hatch and the only one — `MuiAlert.ts` and
        `utils/glass.ts` already use it for exactly this reason.

        Applied at the THEME rather than in a field component deliberately: it reaches
        every `InputBase` in the app in one place — both `components/UI/Input` (which
        `FieldText`, and so every form here, renders) and `@repo/ui`'s `Input` (the
        certificate request-access field, the toolbar search). A rule inside either
        component would have fixed only that component.

        `borderWidth`/`borderStyle` are restated because `!important` on the colour
        alone leaves them to a base rule that a call site can still reset, which would
        produce a coloured border that is zero pixels wide.

        SCOPE — this deliberately wakes ONLY the error border. The dormant `:hover` and
        `.Mui-focused` borders in `components/UI/Input` stay dormant: reviving them
        changes how every field in the app looks on every interaction, which `index.css`
        already calls out as "a deliberate separate change, not a drive-by". An invalid
        field being indistinguishable from a valid one is a correctness defect — the
        user cannot find what is blocking them — while a missing hover tint is polish.
      */
      '&.Mui-error': {
        borderStyle: 'solid',
        borderWidth: '1px',
        borderColor: `${theme.palette.error.main} !important`,
      },
      '.MuiOutlinedInput-notchedOutline': {
        border: '0 none',
      },
      '&.Mui-disabled': {},
      '&.custom-select': {
        padding: '0 !important',
        '& .MuiSelect-select': {
          padding: '12px 20px',
        },
        '& > svg': {
          position: 'absolute',
          right: 10,
        },
      },
      '&.MuiOutlinedInput-root': {
        borderRadius: 0,
        marginTop: '16px',
      },
    }),
  },
}
